import { useEffect, useRef, useState } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesUpload, actesDocuments, actesDocumentLink, actesAddDocument,
  actesRenameDocument, actesRemoveDocument, type ActeDoc,
} from '@/integrations/actesApi';
import { DocViewer } from './DocViewer';
import { actesEn } from '@/lib/actesCountry';

/**
 * LES DOCUMENTS D'UNE DEMANDE — everything that travels with a request that
 * is not the received act itself: an older copy, the first application, a
 * related record.
 *
 * Two modes, as in the original. On an existing demande the list is the
 * database's and every change goes straight through. On a demande that does
 * not exist yet the files are already IN storage but held here, and the parent
 * flushes them once the demande has an id — so nothing is lost if the form is
 * saved, and nothing is orphaned if it is abandoned before the first upload.
 *
 * Renaming and deleting stay with super admins: a file that disappears is not
 * an edit, it is a loss. Five at most, which is the server's rule too.
 */
export type StagedDoc = { file_path: string; file_name: string; file_mime: string; category: string };

const CATEGORIES = [
  { key: 'recu', color: 'emerald', fr: 'Reçu', en: 'Received' },
  { key: 'ancienne', color: 'amber', fr: 'Ancienne copie', en: 'Older copy' },
  { key: 'premiere_demande', color: 'sky', fr: 'Première demande', en: 'First application' },
  { key: 'lie', color: 'violet', fr: 'Acte lié', en: 'Related record' },
  { key: 'autre', color: 'slate', fr: 'Autre', en: 'Other' },
] as const;

const cat = (k: string) => CATEGORIES.find((c) => c.key === k) ?? CATEGORIES[4];

export function ActeDocuments({ lang, acteId, source, canManage = true, isSuperAdmin = false, hideEmpty = false, onStagedChange }: {
  lang: Lang;
  acteId?: string | null;
  /** 'manual' = the related list, 'ai_reception' = what the reception attached. */
  source?: 'manual' | 'ai_reception';
  canManage?: boolean;
  isSuperAdmin?: boolean;
  hideEmpty?: boolean;
  onStagedChange?: (docs: StagedDoc[]) => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const persisted = !!acteId;

  const [docs, setDocs] = useState<ActeDoc[]>([]);
  const [staged, setStaged] = useState<StagedDoc[]>([]);
  const [category, setCategory] = useState<string>('autre');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [ask, setAsk] = useState<{ id: string; name: string } | null>(null);
  const [view, setView] = useState<(() => Promise<{ url: string; name: string | null; mime?: string | null }>) | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!acteId) return;
    let live = true;
    actesDocuments(acteId)
      .then((rows) => { if (live) setDocs(rows.filter((d) => !source || d.source === source)); })
      .catch(() => {});
    return () => { live = false; };
  }, [acteId, source]);

  const onFile = async (f: File) => {
    setBusy(true); setErr('');
    try {
      const up = await actesUpload(f, '_acte_docs');
      if (persisted) {
        const { data } = await actesAddDocument(acteId as string, {
          file_path: up.path, file_name: up.name, file_mime: up.mime, category,
        });
        setDocs((prev) => [data, ...prev]);
      } else {
        const next = [...staged, { file_path: up.path, file_name: up.name, file_mime: up.mime, category }];
        setStaged(next); onStagedChange?.(next);
      }
    } catch (e) {
      const m = e instanceof Error ? e.message : '';
      setErr(m === 'max_related'
        ? T('Cinq documents liés au maximum.', 'Five related documents at most.')
        : friendlyError(e, lang));
    }
    setBusy(false);
  };

  const show = (docId: string) => setView(() => () => actesDocumentLink(acteId as string, docId));

  const rename = async () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    setRenaming(null);
    if (!name) return;
    try {
      const { data } = await actesRenameDocument(acteId as string, renaming.id, name);
      setDocs((prev) => prev.map((d) => (d.id === data.id ? data : d)));
    } catch (e) { setErr(friendlyError(e, lang)); }
  };

  const remove = async () => {
    if (!ask) return;
    setBusy(true);
    try {
      if (persisted) {
        await actesRemoveDocument(acteId as string, ask.id);
        setDocs((prev) => prev.filter((d) => d.id !== ask.id));
      } else {
        const next = staged.filter((s) => s.file_path !== ask.id);
        setStaged(next); onStagedChange?.(next);
      }
      setAsk(null);
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const list = persisted
    ? docs.map((d) => ({ id: d.id, name: d.file_name ?? T('Document', 'Document'), category: d.category, by: d.uploader_name, canView: true }))
    : staged.map((s) => ({ id: s.file_path, name: s.file_name, category: s.category, by: null as string | null, canView: false }));

  if (!canManage && !list.length && hideEmpty) return null;

  return (
    <div className="actes-docs">
      {canManage && (
        <div className="actes-docs-add">
          <select value={category} onChange={(e) => setCategory(e.target.value)} title={T('Catégorie', 'Category')}>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{T(c.fr, c.en)}</option>)}
          </select>
          <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => input.current?.click()}>
            <KBIcon name="documents" size={13} /> {busy ? T('Envoi…', 'Uploading…') : T('Joindre un document', 'Attach a document')}
          </button>
          <input ref={input} type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void onFile(f); }} />
        </div>
      )}

      {err && <p className="muted-note ni-err">⚠ {err}</p>}

      {list.length === 0
        ? (hideEmpty ? null : <p className="muted-note">{T('Aucun document.', 'No document.')}</p>)
        : (
          <ul className="actes-docs-list">
            {list.map((d) => (
              <li key={d.id}>
                <KBIcon name="documents" size={14} />
                {renaming?.id === d.id ? (
                  <input autoFocus className="actes-docs-rename" value={renaming.name}
                    onChange={(e) => setRenaming({ id: d.id, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') void rename(); if (e.key === 'Escape') setRenaming(null); }}
                    onBlur={() => void rename()} />
                ) : (
                  <span className="actes-docs-n" title={d.name}>{d.name}</span>
                )}
                <span className={`actes-tag c-${cat(d.category).color}`}>{T(cat(d.category).fr, cat(d.category).en)}</span>
                {d.by && <span className="dim">· {d.by}</span>}
                {d.canView && (
                  <button type="button" className="actes-row-act" title={T('Voir', 'View')} onClick={() => show(d.id)}>
                    <KBIcon name="eye" size={13} />
                  </button>
                )}
                {isSuperAdmin && persisted && (
                  <button type="button" className="actes-row-act" title={T('Renommer', 'Rename')}
                    onClick={() => setRenaming({ id: d.id, name: d.name })}>
                    <KBIcon name="edit" size={13} />
                  </button>
                )}
                {(isSuperAdmin || !persisted) && (
                  <button type="button" className="actes-row-act danger" title={T('Supprimer', 'Delete')}
                    onClick={() => setAsk({ id: d.id, name: d.name })}>
                    <KBIcon name="trash" size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

      {view && <DocViewer lang={lang} load={view} onClose={() => setView(null)} />}

      {ask && (
        <div className="actes-ask">
          <div className="actes-ask-card">
            <h4>{T('Supprimer ce document ?', 'Delete this document?')}</h4>
            <p className="muted-note">{ask.name}</p>
            <div className="nt-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setAsk(null)}>{T('Annuler', 'Cancel')}</button>
              <button type="button" className="btn" disabled={busy} onClick={() => void remove()}>{T('Supprimer', 'Delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
